/**
 * OnboardingErrorBoundary — IN-2.h
 * ────────────────────────────────
 * Catches uncaught render / write errors inside the onboarding wizard
 * shell so a thrown writer (e.g. corrupt session, observer-mode lock,
 * bad token) never leaves the invitee on a white screen.
 *
 * Hard rules:
 *   • No mutation. No telemetry beyond `logError` (already governed).
 *   • Retry only resets local boundary state; it does not re-run any
 *     onboarding writer.
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logError } from "@/lib/errorLogger";

interface Props {
  children: React.ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  message: string;
}

export class OnboardingErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logError({
      message: error.message,
      stack: error.stack,
      module: "OnboardingWizard",
      severity: "error",
      context: { componentStack: info.componentStack },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: "" });
    this.props.onRetry?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="onboarding-error-boundary"
        className="min-h-screen flex items-center justify-center bg-background p-6"
      >
        <Card className="max-w-md w-full">
          <CardHeader className="flex flex-row items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle>Onboarding hit an unexpected error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Your progress has been saved. Tap retry to continue.</p>
            {import.meta.env.DEV && (
              <pre className="max-h-32 overflow-auto rounded bg-muted p-2 text-left text-[10px]">
                {this.state.message}
              </pre>
            )}
            <Button onClick={this.handleRetry} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" /> Retry
            </Button>
            <p className="text-xs">Need help? Contact your Koredor admin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
}
